import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import HotelIcon from '@material-ui/icons/Hotel';
import LocalCafeIcon from '@material-ui/icons/LocalCafe';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import RoomIcon from '@material-ui/icons/Room';
import { selectCurrentRoute } from '../../store/slices/routesSlice';
import { selectUI, setHoveredStopIdx } from '../../store/slices/uiSlice';
import { t } from '../../i18n';
import SectionTitle from '../common/SectionTitle';
import GlassCard from '../common/GlassCard';
import { reverseGeocode } from '../../utils/mapboxApi';

const useStyles = makeStyles(() => ({
  root: { padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '32px 16px',
    gap: 8,
  },
  stopCard: {
    padding: '10px 12px',
    borderLeft: '3px solid',
    borderRadius: 8,
    marginBottom: 8,
  },
  stopHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  stopTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  stopNumber: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.6rem',
    fontWeight: 700,
  },
  stopBody: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 6,
  },
  metricRow: {
    display: 'flex',
    flexDirection: 'column',
  },
  metricLabel: { fontSize: '0.58rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' },
  metricValue: { fontSize: '0.78rem', color: '#0f172a', fontWeight: 600 },
  addressRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
    marginTop: 6,
    marginBottom: 2,
    paddingTop: 6,
    borderTop: '1px solid rgba(0,0,0,0.05)',
  },
  addressText: {
    fontSize: '0.68rem',
    color: '#475569',
    lineHeight: 1.3,
    fontStyle: 'italic',
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
    padding: '10px 12px',
  },
  regulation: {
    padding: '8px 12px',
    background: 'rgba(59,130,246,0.05)',
    border: '1px solid rgba(59,130,246,0.18)',
    borderRadius: 6,
  },
}));

const STOP_TYPE_KEYS = {
  break_45:           { key: 'stopBreak45',          color: '#f59e0b', icon: LocalCafeIcon },
  break_split_15:     { key: 'stopBreakSplit15',     color: '#3b82f6', icon: LocalCafeIcon },
  break_split_30:     { key: 'stopBreakSplit30',     color: '#3b82f6', icon: LocalCafeIcon },
  daily_rest:         { key: 'stopDailyRest',        color: '#8b5cf6', icon: HotelIcon },
  daily_rest_reduced: { key: 'stopDailyRestReduced', color: '#a855f7', icon: HotelIcon },
  weekly_rest:        { key: 'stopWeeklyRest',       color: '#ef4444', icon: HotelIcon },
};

function Metric({ label, value }) {
  const classes = useStyles();
  return (
    <Box className={classes.metricRow}>
      <Typography className={classes.metricLabel}>{label}</Typography>
      <Typography className={classes.metricValue}>{value}</Typography>
    </Box>
  );
}

function StopsList() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const route = useSelector(selectCurrentRoute);
  const { lang } = useSelector(selectUI);
  const stops = useMemo(() => route.stops || [], [route.stops]);
  const [addresses, setAddresses] = useState({});

  useEffect(() => {
    if (!stops.length || !route.computedRoute?.geometry) return;
    setAddresses({});
    const coords = route.computedRoute.geometry.coordinates;
    stops.forEach((stop, idx) => {
      const coordIdx = Math.round(stop.progressRatio * (coords.length - 1));
      const coord = coords[Math.min(coordIdx, coords.length - 1)];
      if (!coord) return;
      reverseGeocode(coord[0], coord[1]).then(addr => {
        setAddresses(prev => ({ ...prev, [idx]: addr }));
      });
    });
  }, [stops, route.computedRoute]); // eslint-disable-line

  const totalDriveMin = route.computedRoute
    ? Math.round(route.computedRoute.duration / 60)
    : 0;
  const totalRestMin = stops.reduce((sum, s) => sum + (s.duration || 0), 0);

  if (!route.computedRoute) {
    return (
      <Box className={classes.root}>
        <SectionTitle>{t(lang, 'tabStops')}</SectionTitle>
        <Box className={classes.emptyState}>
          <AccessTimeIcon style={{ fontSize: 40, color: '#cbd5e1' }} />
          <Typography style={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center' }}>
            {t(lang, 'calculateForStops')}
          </Typography>
          <Typography style={{ fontSize: '0.65rem', color: '#cbd5e1', textAlign: 'center' }}>
            {t(lang, 'basedOnRegulation')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <SectionTitle icon={AccessTimeIcon}>{t(lang, 'restStops')}</SectionTitle>

      {/* Regulation note */}
      <Box className={classes.regulation}>
        <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', lineHeight: 1.4 }}>
          <strong style={{ color: '#3b82f6' }}>Reg. UE 561/2006</strong> — {t(lang, 'regulationNote')}
        </Typography>
      </Box>

      {/* Summary */}
      <GlassCard flat>
        <Box className={classes.summaryGrid}>
          <Metric label={t(lang, 'totalDrivingTime')} value={`${Math.floor(totalDriveMin / 60)}h ${totalDriveMin % 60}min`} />
          <Metric label={t(lang, 'totalRestTime')} value={`${Math.floor(totalRestMin / 60)}h ${totalRestMin % 60}min`} />
          <Metric label={t(lang, 'stopsCount')} value={stops.length} />
          <Metric
            label={t(lang, 'dailyLimit')}
            value={(() => {
              // Calculate driving per day using daily rest stops as day separators
              const dailyRests = stops.filter(s => s.type && s.type.includes('rest'));
              const numDays = dailyRests.length + 1;
              if (numDays <= 1) {
                return totalDriveMin <= 540 ? '✓ OK (9h)' : totalDriveMin <= 600 ? '✓ OK (10h ext.)' : '⚠ Excede';
              }
              // Build per-day driving minutes
              const dayDrives = [];
              let prevDriveMin = 0;
              stops.forEach(s => {
                if (s.type && s.type.includes('rest')) {
                  dayDrives.push(s.drivingMinBefore - prevDriveMin);
                  prevDriveMin = s.drivingMinBefore;
                }
              });
              dayDrives.push(totalDriveMin - prevDriveMin); // last day
              const allOk = dayDrives.every(d => d <= 600);
              const maxDay = Math.max(...dayDrives);
              const maxH = Math.floor(maxDay / 60);
              const maxM = maxDay % 60;
              return allOk
                ? `✓ ${numDays} ${lang === 'pt' ? 'dias' : 'days'} (máx ${maxH}h${maxM > 0 ? maxM + 'min' : ''})`
                : `⚠ ${lang === 'pt' ? 'Dia excede' : 'Day exceeds'} (${maxH}h${maxM > 0 ? maxM + 'min' : ''})`;
            })()}
          />
        </Box>
      </GlassCard>

      {stops.length === 0 && (
        <Box className={classes.emptyState}>
          <Typography style={{ fontSize: '0.78rem', color: '#22c55e', textAlign: 'center' }}>
            ✓ {t(lang, 'noStopsNeeded')}
          </Typography>
          <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', textAlign: 'center' }}>
            {t(lang, 'within430')}
          </Typography>
        </Box>
      )}

      {/* Stop cards */}
      {stops.map((stop, idx) => {
        const stopTypeCfg = STOP_TYPE_KEYS[stop.type] || STOP_TYPE_KEYS.break_45;
        const stopType = { ...stopTypeCfg, label: t(lang, stopTypeCfg.key) };
        const Icon = stopType.icon;
        return (
          <Box
            key={idx}
            className={classes.stopCard}
            onMouseEnter={() => dispatch(setHoveredStopIdx(idx))}
            onMouseLeave={() => dispatch(setHoveredStopIdx(null))}
            style={{
              borderLeftColor: stopType.color,
              background: `${stopType.color}08`,
              border: `1px solid ${stopType.color}22`,
              borderLeft: `3px solid ${stopType.color}`,
              cursor: 'default',
            }}
          >
            <Box className={classes.stopHeader}>
              <Box className={classes.stopTitle}>
                <Box
                  className={classes.stopNumber}
                  style={{ background: `${stopType.color}22`, color: stopType.color }}
                >
                  {idx + 1}
                </Box>
                <Icon style={{ fontSize: 14, color: stopType.color }} />
                <Typography style={{ fontSize: '0.75rem', fontWeight: 600, color: stopType.color }}>
                  {stopType.label}
                </Typography>
              </Box>
              <Chip
                label={stop.duration >= 60 ? `${Math.floor(stop.duration / 60)}h${stop.duration % 60 > 0 ? stop.duration % 60 + 'min' : ''}` : `${stop.duration}min`}
                size="small"
                style={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  background: `${stopType.color}22`,
                  color: stopType.color,
                }}
              />
            </Box>
            <Box className={classes.stopBody}>
              <Metric label={t(lang, 'driveTime')} value={`${Math.floor(stop.drivingMinBefore / 60)}h ${stop.drivingMinBefore % 60}min`} />
              <Metric label={t(lang, 'kmAtStop')} value={`${stop.km} km`} />
              {stop.arrivalTime && (
                <Metric label={t(lang, 'estimatedArrival')} value={stop.arrivalTime} />
              )}
            </Box>
            <Box className={classes.addressRow}>
              <RoomIcon style={{ fontSize: 12, color: '#94a3b8', marginTop: 1, flexShrink: 0 }} />
              <Typography className={classes.addressText} style={{ flex: 1 }}>
                {addresses[idx] || t(lang, 'fetchingAddress')}
              </Typography>
              {(() => {
                // Use rest area coordinates if available, otherwise route coordinates
                let lat, lng;
                if (stop.suggestedRestArea?.lat && stop.suggestedRestArea?.lon) {
                  lat = stop.suggestedRestArea.lat;
                  lng = stop.suggestedRestArea.lon;
                } else if (stop.coordinates) {
                  lat = stop.coordinates[1];
                  lng = stop.coordinates[0];
                } else if (route.computedRoute?.geometry?.coordinates) {
                  const coords = route.computedRoute.geometry.coordinates;
                  const ci = Math.round(stop.progressRatio * (coords.length - 1));
                  const c = coords[Math.min(ci, coords.length - 1)];
                  if (c) { lng = c[0]; lat = c[1]; }
                }
                if (!lat || !lng) return null;
                return (
                  <span onClick={() => window.dispatchEvent(new CustomEvent('openStreetView', { detail: { lat, lng } }))}
                    title={lang === 'pt' ? 'Ver Street View do local' : 'Street View of location'}
                    style={{ cursor: 'pointer', fontSize: 14, flexShrink: 0, marginLeft: 4, opacity: 0.7, transition: 'opacity 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0.7}>
                    🧍
                  </span>
                );
              })()}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default StopsList;
